import { useCallback, useMemo, useState } from 'react';

import { addDays, today, weekStart } from '../../dates';
import { useGraphql } from '../graphql';
import { useLoad } from '../load';

// Everything Today shows, in one request: this week's records (Monday to
// Sunday, the days to come included), the next appointment, a note the
// patient has not opened yet, the open questionnaires and the clinicians'
// invitations waiting for an answer. now is fixed when
// the page opens, so the week does not move under someone who stays on it
// past midnight. data is undefined until the answer arrives or when it
// failed; error and retry are useLoad's.
//
// markNoteSeen records that the note was opened, so it is not offered again
// on the next visit; the page keeps showing it until then.
export function useToday() {
  const graphql = useGraphql();
  const [ now ] = useState(today);

  const read = useCallback(function() {
    const from = weekStart(now);

    return graphql({
      document: `query Today($from: String!, $to: String!) {
        dailyRecords(from: $from, to: $to) { date completeness }
        nextAppointment { startsAt durationMinutes format clinician questionnaires { submitted total } }
        unseenNote { id body clinician sessionStartsAt }
        assignments { id dueDate questionnaire { name items { position } } draft { id answers { position } } }
        invitations { id sentAt clinician { firstName lastName } }
      }`,
      variables: { from, to: addDays({ day: from, days: 6 }) }
    }).then(function(answer) {
      return { ...answer, today: now };
    });
  }, [ graphql, now ]);

  const { data, error, retry } = useLoad(read);

  const markNoteSeen = useCallback(function(id) {
    return graphql({ document: 'mutation Seen($id: ID!) { markNoteSeen(id: $id) }', variables: { id } });
  }, [ graphql ]);

  return useMemo(function() {
    return { data, error, retry, markNoteSeen };
  }, [ data, error, retry, markNoteSeen ]);
}
